import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  ChevronLeft, ChevronRight, ArrowUpRight, ArrowLeft, Printer, Maximize2,
} from "lucide-react";
import { SLIDES } from "@/components/deck/slides";

export default function Deck() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const printMode = params.get("print") === "1";
  const initial = Math.max(0, Math.min(SLIDES.length - 1, parseInt(params.get("s") || "0", 10) || 0));
  const [idx, setIdx] = useState(initial);
  const [presenting, setPresenting] = useState(false);

  const total = SLIDES.length;
  const current = SLIDES[idx];

  const go = (next) => {
    const n = Math.max(0, Math.min(total - 1, next));
    setIdx(n);
    setParams({ s: String(n) }, { replace: true });
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " " || e.key === "PageDown") { e.preventDefault(); go(idx + 1); }
      else if (e.key === "ArrowLeft" || e.key === "PageUp") { e.preventDefault(); go(idx - 1); }
      else if (e.key === "Home") go(0);
      else if (e.key === "End") go(total - 1);
      else if (e.key === "Escape") setPresenting(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [idx]);

  const enterPresent = async () => {
    setPresenting(true);
    try {
      if (document.documentElement.requestFullscreen) await document.documentElement.requestFullscreen();
    } catch { /* ignore */ }
  };

  const Slide = current.component;

  // Print-all mode: render every slide stacked with page breaks (used by PDF exporter)
  if (printMode) {
    return (
      <main data-testid="deck-print" className="relative bg-[#050A0F] deck-root">
        {SLIDES.map((s, i) => {
          const S = s.component;
          return (
            <section
              key={s.id}
              data-testid={`deck-print-slide-${i + 1}`}
              className="deck-print-slide"
            >
              <S />
            </section>
          );
        })}
        <style>{`
          .deck-print-slide {
            min-height: 100vh;
            page-break-after: always;
            break-after: page;
            position: relative;
          }
          .deck-print-slide:last-child { page-break-after: auto; }
          @page { size: A4 landscape; margin: 0; }
        `}</style>
      </main>
    );
  }

  return (
    <main data-testid="deck-page" className="relative min-h-screen bg-[#050A0F] deck-root">
      {/* Header controls (hidden in print) */}
      <header className="print-hide fixed top-0 inset-x-0 z-40 border-b border-white/5 bg-[#050A0F]/70 backdrop-blur">
        <div className="mx-auto max-w-[1600px] px-4 md:px-6 h-12 flex items-center gap-3">
          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground hover:text-teal"
            data-testid="deck-back"
          >
            <ArrowLeft size={13} /> Site
          </button>
          <div className="h-4 w-px bg-white/10" />
          <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em]">
            <span className="text-teal">Executive Deck</span>
            <span className="text-muted-foreground">·</span>
            <span className="text-muted-foreground">RevGrow Services (UK)</span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <div className="hidden md:flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">
              <span data-testid="deck-slide-index">{String(idx + 1).padStart(2, "0")}</span>
              <span>/</span>
              <span>{String(total).padStart(2, "0")}</span>
            </div>
            <a
              href="/RevGrow_Executive_Deck.pdf"
              download="RevGrow_Executive_Deck.pdf"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="deck-download-pdf"
              className="hidden sm:inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground hover:text-teal px-3 py-1.5 rounded-full border border-white/10 hover:border-teal/40"
            >
              <Printer size={12} /> Download PDF
            </a>
            <button
              onClick={enterPresent}
              data-testid="deck-present"
              className="hidden sm:inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground hover:text-teal px-3 py-1.5 rounded-full border border-white/10 hover:border-teal/40"
            >
              <Maximize2 size={12} /> Present
            </button>
          </div>
        </div>
        {/* Progress bar */}
        <div className="h-[2px] w-full bg-white/5">
          <div
            className="h-full bg-teal transition-all duration-500 ease-out"
            style={{ width: `${((idx + 1) / total) * 100}%` }}
            aria-hidden
          />
        </div>
      </header>

      {/* Slide surface */}
      <section
        key={idx}
        className="relative pt-12 min-h-screen deck-slide-in"
        data-testid={`deck-slide-${idx + 1}`}
      >
        <Slide />
      </section>

      {/* Slide nav */}
      <nav className="print-hide fixed inset-x-0 bottom-0 z-40 border-t border-white/5 bg-[#050A0F]/85 backdrop-blur">
        <div className="mx-auto max-w-[1600px] px-4 md:px-6 h-14 flex items-center gap-3">
          <button
            data-testid="deck-prev"
            onClick={() => go(idx - 1)}
            disabled={idx === 0}
            className="h-9 w-9 rounded-full border border-white/10 hover:border-teal/40 grid place-items-center disabled:opacity-30"
            aria-label="Previous slide"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            data-testid="deck-next"
            onClick={() => go(idx + 1)}
            disabled={idx === total - 1}
            className="h-9 w-9 rounded-full bg-teal text-[#050A0F] grid place-items-center disabled:opacity-40"
            aria-label="Next slide"
          >
            <ChevronRight size={16} />
          </button>

          {/* Dots */}
          <div className="flex items-center gap-1.5 mx-auto overflow-x-auto no-scrollbar">
            {SLIDES.map((s, i) => (
              <button
                key={s.id}
                data-testid={`deck-dot-${i + 1}`}
                onClick={() => go(i)}
                title={s.title}
                className={`h-1.5 rounded-full transition-all ${
                  i === idx ? "w-8 bg-teal" : "w-1.5 bg-white/20 hover:bg-white/40"
                }`}
              />
            ))}
          </div>

          <div className="hidden md:block text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground max-w-[280px] truncate">
            {current.title}
          </div>
          <Link
            to="/#contact"
            className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-teal/10 border border-teal/30 text-teal px-3 py-1.5 text-xs font-medium hover:bg-teal/20 transition"
          >
            Book a call <ArrowUpRight size={12} />
          </Link>
        </div>
      </nav>

      <style>{`
        @keyframes slideIn {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .deck-slide-in { animation: slideIn 480ms cubic-bezier(0.22, 1, 0.36, 1); }

        @media print {
          .print-hide { display: none !important; }
          body, .deck-root { background: white !important; color: black !important; }
          .deck-slide-in { animation: none !important; }
          section[data-testid^="deck-slide-"] {
            page-break-after: always;
            min-height: auto !important;
          }
        }
      `}</style>
    </main>
  );
}
